import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Alert, Spinner } from 'react-bootstrap';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  sprayService,
  Orchard,
  SprayMachine,
} from '../../services/sprayService';
import {
  refreshMyOperatorCombos,
  readCachedOperatorCombos,
} from '../../services/operatorSpraySessionCacheService';
import { db } from '../../db';
import type { CachedOperatorCombo, SpraySyncError } from '../../db';
import { getPrimaryOrchardId } from '../../utils/spraySessionDisplay';
import PageHeader from '../shell/PageHeader';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { RowCard, StatusPill, SyncPill } from '../patterns';
import { SessionStatusPill } from './statusPills';
import { formatDate } from '../../dateFormat';
import './OperatorSessionList.css';

const KNOWN_SYNC_ERROR_CODES = new Set([
  'invalid_state',
  'not_assigned',
  'conflict',
  'server_error',
]);

const NON_SYNCED_STATUSES = ['pending', 'failed', 'syncing'] as const;

/**
 * Reduce a JSON-serialised Mongoose Date (`'2026-05-01T00:00:00.000Z'`) or
 * a plain YYYY-MM-DD string down to just the YYYY-MM-DD portion for display.
 * The time component is noise in Field Mode — operators only ever need the
 * calendar date of the scheduled session.
 */
function formatSessionDate(value: string | undefined | null): string {
  if (!value) return '';
  return formatDate(value, { timeZone: 'UTC' });
}

/**
 * Render the combo's local-time start moment as "HH:MM, YYYY-MM-DD" for the
 * Started line on in_progress rows. Pads to two digits per spec §3.1.
 */
function formatStartedTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const HH = String(d.getHours()).padStart(2, '0');
  const MM = String(d.getMinutes()).padStart(2, '0');
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${HH}:${MM}, ${yyyy}-${mm}-${dd}`;
}

/**
 * OperatorSessionList — the operator's actionable assignment list, surfaced
 * in the Spray module via the App Shell sidebar ("My Sessions").
 *
 * Shows ONE ROW PER COMBO (per `OperatorSpraySession`) with status ∈
 * {not_started, in_progress}, scoped server-side to the caller's linked
 * SprayOperator. Each row deep-links to the per-combo work flow at
 * `/protection/operator-sessions/:comboId`. The component is mounted behind
 * `<RequireLinkedOperator>` in App.tsx, so a pure-manager caller never
 * reaches it (they're redirected to /forbidden).
 *
 * Designed for outdoor legibility with large touch targets (per spec §2).
 * The `.operator-session-list` container class lets CSS Container Queries
 * dial up contrast and sizing without polluting the admin list.
 *
 * Phase 6 / Task 6.12 (multi-operator spray sessions track):
 *   - Data source moved from a per-render network fetch to the Dexie
 *     `operatorCombos` cache via useLiveQuery. The component triggers a
 *     SILENT background refresh on mount; failure surfaces only when the
 *     cache is also empty.
 *   - Row anatomy redesigned (spec §3.1): identity (SES-{seq} · name),
 *     date, orchard · machine, started, late-signoff banner, right rail
 *     with status pill + queued + late-signoff + action affordance.
 *   - Queued modifier: rows with at least one pending entry in
 *     `operatorComboStarts` or `operatorComboCompletes` get a blue left
 *     border and a queued SyncPill in the rail.
 *   - Late-signoff modifier: rows whose combo is in_progress and whose
 *     parent session is cancelled get a red left border (CSS gives this
 *     precedence over queued) and an inline danger banner.
 */
const OperatorSessionList: React.FC = () => {
  const { t } = useTranslation();

  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('spray.title'), to: '/protection' },
    { label: t('spray.operatorSessions.title') },
  ]);

  // Live-query the cache so the list re-renders whenever the cache is
  // refreshed OR a queue write flips the queued state of a row. The hook
  // returns `undefined` on first render until the initial query resolves,
  // then the resolved array on subsequent renders.
  const cachedCombos: CachedOperatorCombo[] | undefined = useLiveQuery(
    () => readCachedOperatorCombos(),
    [],
  );

  // Set of combo IDs with at least one pending entry in either queue.
  // Used to compute the queued modifier per row. The empty-set default
  // means the queued state simply absent until the first query resolves.
  const pendingStartComboIds = useLiveQuery(
    async () => {
      const rows = await db.operatorComboStarts
        .where('syncStatus')
        .anyOf([...NON_SYNCED_STATUSES])
        .toArray();
      return new Set(rows.map(r => r.comboId));
    },
    [],
    new Set<string>(),
  );
  const pendingCompleteComboIds = useLiveQuery(
    async () => {
      const rows = await db.operatorComboCompletes
        .where('syncStatus')
        .anyOf([...NON_SYNCED_STATUSES])
        .toArray();
      return new Set(rows.map(r => r.comboId));
    },
    [],
    new Set<string>(),
  );

  const [hasLinkedOperator, setHasLinkedOperator] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [orchards, setOrchards] = useState<Orchard[]>([]);
  const [machines, setMachines] = useState<SprayMachine[]>([]);
  const [syncErrors, setSyncErrors] = useState<SpraySyncError[]>([]);

  const dismissSyncError = async (id: number | undefined) => {
    if (id == null) return;
    await db.spraySyncErrors.delete(id);
    setSyncErrors(prev => prev.filter(e => e.id !== id));
  };

  const renderSyncError = (e: SpraySyncError) => {
    const localised = KNOWN_SYNC_ERROR_CODES.has(e.errorCode)
      ? t(`spray.operatorSessions.syncErrors.${e.errorCode}`)
      : e.message;
    return (
      <Alert
        key={e.id}
        variant="warning"
        dismissible
        onClose={() => dismissSyncError(e.id)}
      >
        {localised}
      </Alert>
    );
  };

  useEffect(() => {
    let cancelled = false;

    // Orchards + machines are required to render row context. Kick them
    // off in parallel with the refresh below.
    const orchardsPromise = sprayService
      .getOrchards()
      .catch(() => null as Orchard[] | null);
    const machinesPromise = sprayService
      .getMachines()
      .catch(() => null as SprayMachine[] | null);

    (async () => {
      // Surface any pre-existing sync-error rows immediately. These come
      // from background drainers (e.g. queued offline sign-offs) and are
      // orthogonal to the refresh below.
      try {
        const errs = await db.spraySyncErrors.toArray();
        if (!cancelled) setSyncErrors(errs);
      } catch {
        // Non-fatal — banner just won't appear.
      }

      try {
        const result = await refreshMyOperatorCombos();
        if (cancelled) return;
        setHasLinkedOperator(result.hasLinkedOperator);
        setError('');
      } catch (err: any) {
        if (cancelled) return;
        // The refresh path is silent-on-failure as long as the cache has
        // something to show. We check the cache directly (not via the
        // live-query, which may not have hydrated yet) so the decision is
        // immediate.
        const cachedCount = await db.operatorCombos.count();
        if (cachedCount === 0) {
          setError(err?.message ?? t('spray.operatorSessions.loadError'));
        }
      }

      if (!cancelled) setLoading(false);

      const orch = await orchardsPromise;
      if (!cancelled && orch) setOrchards(orch);
      const mach = await machinesPromise;
      if (!cancelled && mach) setMachines(mach);
    })();

    return () => {
      cancelled = true;
    };
  }, [t]);

  const orchardNameById = useMemo(
    () => new Map(orchards.map(o => [o._id, o.orchardName])),
    [orchards],
  );

  const machineNameById = useMemo(
    () => new Map(machines.map(m => [m._id, m.name])),
    [machines],
  );

  /**
   * Set of `${parentId}::${machineId}` keys flagged as "shared" — i.e. two
   * or more combos on the SAME PARENT use the same machine, signalling a
   * handover between operators within that session. Two combos on
   * DIFFERENT parents that happen to use the same machine do NOT count as
   * shared, so the set is keyed by `(parent, machine)` not by machine
   * alone. Mirrors ComboSummaryList.buildSharedMachineSet but scoped per
   * parent because the "My Sessions" list spans many parents.
   */
  const sharedKeys = useMemo(() => {
    const counts = new Map<string, number>();
    for (const c of cachedCombos ?? []) {
      if (!c.machineId) continue;
      const key = `${c.parentId}::${c.machineId}`;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    const shared = new Set<string>();
    counts.forEach((count, key) => {
      if (count >= 2) shared.add(key);
    });
    return shared;
  }, [cachedCombos]);

  // Always render PageHeader so the title and breadcrumb are present even in
  // loading / error / empty states.
  const comboCount = cachedCombos?.length ?? 0;
  const header = (
    <PageHeader
      title={t('spray.operatorSessions.title')}
      meta={loading ? undefined : t('spray.operatorSessions.metaCount', { count: comboCount })}
    />
  );

  // Loading state: the live-query hasn't returned yet AND the refresh
  // hasn't resolved/errored either. Once either condition flips we move
  // out of loading.
  if (loading && (cachedCombos === undefined || cachedCombos.length === 0)) {
    return (
      <div className="operator-session-list">
        {header}
        <div className="text-center p-4">
          <Spinner animation="border" role="status" />
          <div className="mt-2">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  if (error && comboCount === 0) {
    return (
      <div className="operator-session-list">
        {header}
        {syncErrors.map(renderSyncError)}
        <Alert variant="danger">{error}</Alert>
      </div>
    );
  }

  if (!hasLinkedOperator && comboCount === 0) {
    return (
      <div className="operator-session-list">
        {header}
        {syncErrors.map(renderSyncError)}
        <Alert variant="warning">
          {t('spray.operatorSessions.notLinkedToOperator')}
        </Alert>
      </div>
    );
  }

  if (comboCount === 0) {
    return (
      <div className="operator-session-list">
        {header}
        {syncErrors.map(renderSyncError)}
        <Alert variant="info">{t('spray.operatorSessions.noSessions')}</Alert>
      </div>
    );
  }

  return (
    <div className="operator-session-list">
      {header}
      {syncErrors.map(renderSyncError)}
      <div className="d-flex flex-column gap-3 mt-3">
        {(cachedCombos ?? []).map(combo => {
          const parent = combo.sessionId;
          // The util accepts anything with an `orchardIds` array — the
          // cached subdocument satisfies that contract, so we pass it
          // through directly without re-shaping into a full SpraySession.
          const orchardId = getPrimaryOrchardId(parent as any);
          const orchardName = (orchardId && orchardNameById.get(orchardId)) || '—';
          const programName = parent.programId?.name || '—';
          const dateDisplay = formatSessionDate(parent.date) || '—';
          const status = combo.status;
          const actionLabel =
            status === 'not_started'
              ? t('spray.operatorSessions.actionStart')
              : t('spray.operatorSessions.actionContinue');
          // Resolve the row's machine label. Fall back to '—' when the
          // machine list hasn't resolved or the id is unknown — matches
          // the orchard fallback above so an unresolvable lookup never
          // breaks the row layout. Append the localised "(shared)"
          // suffix when this combo's (parent, machine) pair occurs 2+
          // times — i.e. another combo on the same parent is using the
          // same machine, which signals a handover.
          const rawMachineName = combo.machineId
            ? machineNameById.get(combo.machineId)
            : undefined;
          const isShared = combo.machineId
            ? sharedKeys.has(`${combo.parentId}::${combo.machineId}`)
            : false;
          const machineLabel = rawMachineName
            ? (isShared
                ? `${rawMachineName} ${t('spray.sessions.combos.sharedSuffix')}`
                : rawMachineName)
            : '—';
          const isLateSignOff =
            status === 'in_progress' && parent.status === 'cancelled';
          const isQueued =
            (pendingStartComboIds?.has(combo._id) ?? false) ||
            (pendingCompleteComboIds?.has(combo._id) ?? false);
          // Build the identity line — only render when we have at least
          // a seq value (pre-Phase-6 sessions without seq omit the line
          // entirely per spec §3.1).
          const seq = parent.seq;
          const name = parent.name;
          const identityLine =
            seq !== undefined && seq !== null
              ? name
                ? `SES-${seq} · ${name}`
                : `SES-${seq}`
              : null;
          return (
            <Link
              key={combo._id}
              to={`/protection/operator-sessions/${combo._id}`}
              className="operator-session-card-link text-decoration-none text-dark"
            >
              <RowCard
                lateSignoff={isLateSignOff}
                queued={isQueued}
                testId={`row-card-${combo._id}`}
              >
                <div className="operator-session-card__body">
                  {identityLine && (
                    <div className="operator-session-card__identity">
                      {identityLine}
                    </div>
                  )}
                  <div className="operator-session-card__date">{dateDisplay}</div>
                  <div className="operator-session-card__row-sub">
                    {orchardName}
                  </div>
                  <div className="operator-session-card__program">
                    {programName}
                  </div>
                  <div className="operator-session-card__machine">
                    {machineLabel}
                  </div>
                  {combo.startedAt && combo.status === 'in_progress' && (
                    <div className="operator-session-card__started">
                      {t('spray.operatorSessions.started', {
                        time: formatStartedTime(combo.startedAt),
                      })}
                    </div>
                  )}
                  {isLateSignOff && (
                    <div className="operator-session-card__late-signoff">
                      {t('spray.operatorSessions.lateSignOffBanner')}
                    </div>
                  )}
                </div>
                <div className="operator-session-card__rail">
                  <SessionStatusPill status={status} />
                  {isLateSignOff && (
                    <StatusPill variant="danger">
                      {t('spray.operatorSessions.lateSignOffPill')}
                    </StatusPill>
                  )}
                  {isQueued && (
                    <SyncPill
                      tone="queued"
                      label={t('spray.operatorSessions.queuedPill')}
                    />
                  )}
                  <div className="operator-session-card__action">
                    {actionLabel}
                  </div>
                </div>
              </RowCard>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default OperatorSessionList;
